/**
 * Talking to the DrBell API from the caregiver dashboard.
 *
 * Session cookies, not a shared secret. This app holds no credential of its
 * own: it is a view of what the signed-in person's grants already permit, and
 * every route re-resolves that server-side.
 */

import type { Entitlements, MealAnchor, Measure } from '@drbell/core';

export type { MealAnchor };

/** Same-origin by default: this Worker proxies /api/* to the API. */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The refusal's own body, when it was JSON: `routine_time_in_use` names the medicines in it. */
    readonly body: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The server's error code (`routine_time_in_use`, `limit_reached`…), when it sent one. */
  get code(): string | null {
    return typeof this.body?.error === 'string' ? this.body.error : null;
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });

  if (!response.ok) {
    let detail = response.statusText;
    let raw: Record<string, unknown> | null = null;
    try {
      const body = (await response.json()) as { error?: string; message?: string; used?: number; limit?: number };
      raw = body as Record<string, unknown>;
      // The server's own sentence wins over its error code — see the note in
      // the organisation console's client.
      // Only ever text: an object here printed "[object Object]" (defect 142).
      detail = typeof body.message === 'string' ? body.message : typeof body.error === 'string' ? body.error : detail;
      // Seat and entitlement refusals carry the numbers that explain them.
      if (body.used !== undefined && body.limit !== undefined) {
        detail = `${detail} (${body.used} of ${body.limit} seats in use)`;
      }
    } catch {
      /* body was not JSON */
    }
    throw new ApiError(response.status, detail, raw);
  }
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  /** A body only where the route reads one: deleting a routine time says where its medicines go. */
  del: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'DELETE', ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
  /**
   * A photograph: the image itself is the body, with its own content type.
   * `call` would label it JSON and the media route would refuse it.
   */
  upload: async <T>(path: string, file: File): Promise<T> => {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': file.type },
      body: file,
    });
    if (!response.ok) {
      let detail = response.statusText;
      try {
        const body = (await response.json()) as { error?: string; message?: string };
        // Only ever text: an object here printed "[object Object]" (defect 142).
      detail = typeof body.message === 'string' ? body.message : typeof body.error === 'string' ? body.error : detail;
      } catch {
        /* body was not JSON */
      }
      throw new ApiError(response.status, detail);
    }
    return (await response.json()) as T;
  },
};

// ── Shapes the dashboard reads ──────────────────────────────────────────────

export interface Me {
  /** `locale` is the person's language: the footer offers "Report a wrong word" when it is not English (docs/31). */
  user: { id: string; name: string | null; phoneNumber: string | null; locale?: string } | null;
  patients: PatientSummary[];
  pendingConsents: { id: string; version: number }[];
}

export interface PatientSummary {
  id: string;
  displayName: string;
  timezone: string;
  locale: string;
  presentationMode: 'elder' | 'standard' | 'kid';
  ownerType: 'user' | 'org';
  /**
   * Who owns the record: a user id or an organisation id. Compared with the
   * signed-in id to tell "somebody else set my record up" (take it back) from
   * "it is already mine".
   */
  ownerId?: string;
  /** First name of whoever set up a record about you (only on your own record). */
  ownerName?: string | null;
  /** This record is the signed-in person's own. */
  isSelf?: boolean;
  /** Whoever set this record up is told about its missed doses (defect 139). */
  ownerIsTold?: boolean;
}

export type DoseStatus = 'pending' | 'taken' | 'skipped' | 'missed' | 'refused';

export interface TimelineDose {
  /**
   * Before food, after food, on an empty stomach — RX-MED-02.
   *
   * On the dose rather than the medication, because it belongs to the
   * schedule: the same drug can be prescribed differently on two courses.
   */
  foodRelation?: 'before_food' | 'with_food' | 'after_food' | 'empty_stomach' | 'any';
  dose: {
    id: string;
    dueAt: number;
    windowEnd: number;
    status: DoseStatus;
    doseQuantity: number;
    doseUnit: string;
    takenAt: number | null;
    snoozeCount: number;
    escalationStage: number;
    source: string | null;
    note: string | null;
    /** "I'll handle it" — RX-CARE-12. Who said so, and when. */
    claimedAt: number | null;
    claimedByUserId: string | null;
    /** Put off by hand to a later time today (docs/33 C9): its usual time. */
    postponedFrom?: number | null;
    medicationId?: string;
  };
  medication: {
    name: string;
    /** A second label the user added themselves. Shown after, never instead. */
    nameAlt: string | null;
    strength: string | null;
    strengthUnit: string | null;
    stripImageKey: string | null;
    isCritical: boolean;
    /** A measurement the doctor asked for (docs/33 D1) is answered by typing the number. */
    kind?: 'medicine' | 'diet' | 'measurement';
    measure?: Measure | null;
    measureUnit?: string | null;
  };
}

/** Matches `GET /patients/:id/adherence` exactly. Do not invent this shape. */
export interface AdherenceSummary {
  days: number;
  total: number;
  truncated: boolean;
  byMedication: {
    medicationId: string;
    medicationName: string;
    strength: string | null;
    strengthUnit: string | null;
    /** Keyed by dose status; a status with no doses is absent, not zero. */
    counts: Record<string, number>;
  }[];
}


// ── Adherence report and sharing (RX-CARE-09) ───────────────────────────────

export interface Counts {
  taken: number;
  skipped: number;
  missed: number;
  refused: number;
  pending: number;
  resolved: number;
}

export type PartOfDay = 'morning' | 'afternoon' | 'evening' | 'night';

export interface MedicationAdherence {
  medicationId: string;
  medicationName: string;
  counts: Counts;
  /** taken / resolved, or null when nothing has resolved yet. */
  rate: number | null;
  byPartOfDay: Record<PartOfDay, Counts>;
  currentStreak: number;
  longestStreak: number;
  recentMisses: Array<{ dueAt: number; localTime: string }>;
  takenAfterSnooze: number;
}

export interface AdherenceReport {
  from: number;
  to: number;
  timeZone: string;
  overall: Counts;
  overallRate: number | null;
  medications: MedicationAdherence[];
  perfectDays: number;
  daysCovered: number;
}

export interface ReportResponse {
  report: AdherenceReport;
  patientName: string;
  /** The query hit its row cap, so the numbers are an undercount. */
  truncated: boolean;
  tier: string;
  requestedDays: number;
  days: number;
  clampedByTier?: boolean;
}

export interface ReportShare {
  id: string;
  fromAt: number;
  toAt: number;
  note: string | null;
  createdAt: number;
  expiresAt: number;
  revokedAt: number | null;
  viewCount: number;
  lastViewedAt: number | null;
}

// ── Invitations (RX-CARE-02) and a child's own reports (RX-A11Y-03) ────────

export type AccessRole = 'caregiver' | 'guardian' | 'viewer' | 'clinician' | 'staff' | 'self';

/** `GET /invites` — invitations waiting for this person's decision. */
export interface PendingInvite {
  id: string;
  patientId: string;
  role: AccessRole;
  inviteToken: string | null;
  createdAt: number;
  invitedAt?: number;
  inviteExpiresAt?: number | null;
}

/** The ways somebody can be told about a dose. */
export type Channel = 'push' | 'whatsapp' | 'sms' | 'email' | 'voice';

/**
 * `GET /invites/:token` — the Approve / Reject screen's content.
 *
 * Public, so it works before sign-in: first names, the inviter's masked
 * number, what would be shared and how. The server re-derives all of it on
 * accept, so nothing here is trusted back.
 */
export interface InvitePreview {
  version: number;
  role: AccessRole;
  patientName: string;
  inviterName: string | null;
  /** Masked: +91•••••123. */
  inviterPhone: string | null;
  alerts: { taken: boolean; late: boolean; missed: boolean };
  /** Offered, within the record's plan. Empty for `self`. */
  channels: Channel[];
  tier: 'free' | 'plus' | 'family' | 'org';
  /** False on Free: they would see the record but never be told (owner, 20 Oct 2026). Absent from older servers. */
  caregiverAlerts?: boolean;
  /** A hands-on helper: the reminders will ring on their phone. Absent from older servers. */
  ringsHere?: boolean;
  expiresAt: number | null;
  status: string;
  /** The older name for `inviterName`. */
  invitedBy: string | null;
  /** The number the invitation was sent to, masked. */
  invitePhoneHint: string | null;
}

/** `POST /invites/:token/accept`. */
export type InviteAccepted =
  | { ok: true; patientId: string; role: AccessRole; channels: Channel[]; alreadyAccepted?: boolean }
  | { ok: true; patientId: string; linkedAsSelf: true };

/** `POST /patients/:id/grants` — the invitation, and the link to send it by hand. */
export interface InviteSent {
  ok: boolean;
  status: 'pending';
  expiresAt: number;
  /** Absolute: only the API knows which host serves `/i/:token`. */
  shareUrl: string;
  /** They already use DrBell and were told in the app. */
  notifiedInApp: boolean;
}

// ── The statement (a bank statement for a record, or for an account) ───────

export interface StatementLine {
  id: string;
  at: number;
  /** Already in the reader's zone and language. */
  when: string;
  text: string;
  by: string;
  from: string;
  kind: 'event' | 'alert';
  event: string;
}

/** `GET /patients/:id/statement` and `GET /me/statement`. Every string is rendered server-side. */
export interface Statement {
  title: string;
  subject: string | null;
  period: { from: number; to: number; label: string };
  timezone: string;
  locale: string;
  summary: { taken: number; missed: number; skipped: number; alerts: number; labels: string[] };
  lines: StatementLine[];
  truncated: boolean;
  generatedAt: number;
  footer: string;
  columns: { date: string; what: string; by: string; from: string };
  empty: string;
}

/** `GET /patients/:id/doses/unconfirmed` — a child said "taken"; a grown-up confirms. */
export interface UnconfirmedDose {
  id: string;
  dueAt: number;
  takenAt: number | null;
  doseQuantity: number;
  doseUnit: string;
  medicationName: string;
  medicationNameAlt: string | null;
}

/** What the signed-in person may do on one record, from `GET /patients/:id`. */
export interface Access {
  capabilities: string[];
  role: string | null;
  via: string | null;
}

// ── Medicines (RX-MED-11) and appointments (RX-RX-05) ────────────────────

/** `GET /patients/:id/medications`, as the phone reads it. */
export interface MedicationEntry {
  medication: {
    id: string;
    name: string;
    brandName: string | null;
    strength: string | null;
    strengthUnit: string | null;
    form: string;
    stripImageKey: string | null;
    isCritical: boolean;
    instructionsText: string | null;
    stockCount: number | null;
    stockUnit: string | null;
    refillThreshold: number | null;
    /** Paused (docs/33 C5): since when, the last paused day (YYYY-MM-DD) or null for "until resumed", and why. */
    pausedAt?: number | null;
    pausedUntil?: string | null;
    pauseReason?: 'hospital' | 'travel' | 'doctor' | 'other' | null;
  };
  schedules: {
    id: string;
    type: 'fixed_times' | 'meal_relative' | 'interval' | 'prn';
    /** The doctor's own shorthand, shown verbatim and never translated. */
    sourceNotation: string | null;
    /** What a meal-relative schedule is tied to: meals, and own times of day (`time:<id>`). */
    anchors?: MealAnchor[] | null;
    foodRelation: string | null;
    /** How much at each time, unless an anchor says otherwise, and in what. */
    doseQuantity?: number;
    doseUnit?: string;
    /** Weekdays it is taken on (1 = Monday); null for every day. */
    daysOfWeek?: number[] | null;
    /** The course, in the person's own calendar; the end is inclusive. */
    startDate: string;
    endDate: string | null;
    archivedAt: number | null;
  }[];
}

export interface Appointment {
  id: string;
  kind: 'doctor_visit' | 'lab_test' | 'procedure' | 'other';
  title: string;
  doctorName: string | null;
  locationNote: string | null;
  notes: string | null;
  localDate: string;
  localTime: string | null;
  status: 'scheduled' | 'attended' | 'missed' | 'cancelled';
}

/** `GET /patients/:id/grants` — who can see this record, and how they hear. */
export interface GrantSummary {
  id: string;
  granteeType: 'user' | 'org';
  granteeId: string;
  role: AccessRole;
  notifyOnTaken: boolean;
  notifyOnLate: boolean;
  notifyOnMissed: boolean;
  status: 'pending' | 'active' | 'revoked' | 'expired';
  invitedAt: number;
  acceptedAt: number | null;
  /** Masked unless the caller can manage grants. */
  invitePhone: string | null;
  /** First name of an account holder; null for an invitation or a phone-number name. */
  granteeName?: string | null;
  /** A hands-on helper: the reminders ring on their phone too. */
  ringsHere?: boolean;
  /**
   * Asked to have the reminders ring on their phone and not answered yet:
   * when the question lapses. Nothing rings until they accept.
   */
  ringsHereRequestExpiresAt?: number | null;
}

// ── How I'm told (a caregiver's own say over their alerts) ─────────────────

/** The channels a caregiver can choose. Never voice: no screen offers it. */
export type CaringChannel = Exclude<Channel, 'voice'>;

export interface QuietHours {
  /** `HH:MM`, on the caregiver's own clock. */
  from: string;
  to: string;
}

/** One entry of `GET /me/caring`: somebody this person helps, and how they hear. */
export interface CaringEntry {
  grantId: string;
  patientId: string;
  patientName: string;
  role: AccessRole;
  /** What the record's plan lets them choose from. */
  offered: CaringChannel[];
  /** False on Free: nothing here reaches them until the plan is paid for. Absent from older servers. */
  caregiverAlerts?: boolean;
  /** What they have chosen; always within `offered`. */
  channels: CaringChannel[];
  /** A missed dose is always on: it is why they were asked. */
  /** `childReport`: a child's record — asked to confirm each dose the child reports (absent from older servers). */
  alerts: { missed: true; late: boolean; taken: boolean; childReport?: boolean };
  /** A child's record (kid mode): the confirm switch is shown. */
  kid?: boolean;
  quietHours: QuietHours | null;
  /** The reminders ring on my phone: I give the medicines. */
  ringsHere?: boolean;
  /**
   * Whether ringing is something this grant can have: a caregiver or
   * guardian of a family's record. Mine to turn off; never mine alone to
   * turn on — the family asks (`ringingRequest`), and I accept.
   */
  mayRing?: boolean;
  /** The family asked for the reminders to ring on my phone: Accept / Decline. */
  ringingRequest?: RingingRequest | null;
  /** Who to ask, when it does not ring and nobody asked: a first name, or null. */
  askName?: string | null;
}

/** "Ring {patient}'s reminders on your phone?" — asked by whoever manages access. */
export interface RingingRequest {
  askedAt: number;
  /** Unanswered by then, it lapses. */
  expiresAt: number;
  /** First name of whoever asked; null when they never gave one. */
  askerName: string | null;
}

/** `PUT /me/caring/:grantId`. */
export interface CaringUpdate {
  channels: CaringChannel[];
  late: boolean;
  taken: boolean;
  /** A child's record only: confirm each dose the child reports. */
  childReport?: boolean;
  quietHours: QuietHours | null;
  /** Sent only where the switch is offered (`mayRing`). */
  ringsHere?: boolean;
}

/** `GET /patients/:id/phones` — the phones a record rings on, as the website reads them. */
export interface RingingPhones {
  phones: { id: string; label: string; rings?: boolean }[];
  /** Some of the holder's phones were left out of ringing on purpose ("only this phone"). */
  someOnly?: boolean;
}

/** `GET /patients/:id/prescriptions` — photographs of the slips. */
export interface PrescriptionPhoto {
  id: string;
  url: string;
  doctorName: string | null;
  createdAt: number;
}

// ── A record's own settings and routine (the website as a home for your own reminders) ──

/** `GET /patients/:id` — the record, its meal and own times, and what the reader may do on it. */
export interface PatientDetail {
  /** First name of whoever set up a record about the reader; null otherwise. */
  ownerName?: string | null;
  patient: {
    id: string;
    displayName: string;
    givenName?: string | null;
    familyName?: string | null;
    dateOfBirth?: string | null;
    address?: string | null;
    timezone: string;
    locale: string;
    presentationMode: 'elder' | 'standard' | 'kid';
    /** Set when the record is linked to the person's own phone. */
    selfUserId?: string | null;
    ownerType?: 'user' | 'org';
    ownerId?: string;
  };
  /** Null until somebody sets them; the scheduler then uses the defaults. */
  mealTimes: MealTimes | null;
  /** Their own named times of day besides the meals. Absent from an older server. */
  routineTimes?: RoutineTime[];
  access: Access;
}

export interface MealTimes {
  breakfast: string;
  lunch: string;
  dinner: string;
  bedtime: string;
}

/** What the scheduler assumes when nobody has set meal times — as the phone. */
export const DEFAULT_MEAL_TIMES: MealTimes = { breakfast: '08:00', lunch: '13:00', dinner: '20:00', bedtime: '22:00' };

/** One of a person's own named times of day — "Evening tea, 17:00". The name as typed. */
export interface RoutineTime {
  id: string;
  name: string;
  /** `HH:MM`, in the person's own zone. */
  time: string;
}

/** `GET /consent` — one notice, as it will be shown. */
export interface ConsentDocument {
  id: string;
  version: number;
  title: string;
  requiredBeforeFirstUse: boolean;
  accepted: boolean;
  /** The language the body is in (English when the requested one is not approved). */
  locale: string;
  body: string[];
}

export type Tier = 'free' | 'plus' | 'family' | 'org';

/** `GET /billing/subscription` — what this person has, and what it lets them do. */
export interface SubscriptionView {
  tier: Tier;
  entitlements: Entitlements;
  /** What they have, in English, from the version they bought. */
  lines: Array<{ key: string; values: Record<string, string | number>; text: string; channels?: string[] }>;
  subscription: {
    status: string;
    /** `google`, `apple`, `complimentary`, `manual`, or a web gateway's slug. */
    store?: string | null;
    /** Seconds, not milliseconds: the stores' own unit. */
    currentPeriodEnd: number | null;
    cancelAtPeriodEnd: boolean;
    startedAt: number;
    planVersion: number | null;
    keepsOlderTerms: boolean;
  } | null;
}

